/**
 * Data hooks for release notes.
 *
 * None of these re-fetch when the language changes: the payload carries EN and
 * DE, and the components pick at render time. The dependency arrays below are
 * deliberately free of anything i18n.
 */

import { useCallback, useEffect, useState } from "react";
import {
  releaseNotesApi,
  ReleaseNotesApiError,
} from "@services/releaseNotesApi";
import type { Module, PublicReleaseNote } from "@models/releaseNotes";

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

const initialState = <T,>(): AsyncState<T> => ({
  data: null,
  loading: true,
  error: null,
});

export function useReleaseNotes(module?: Module) {
  const [state, setState] = useState<AsyncState<PublicReleaseNote[]>>(
    initialState,
  );

  const load = useCallback(() => {
    let cancelled = false;
    setState((previous) => ({ ...previous, loading: true, error: null }));

    releaseNotesApi
      .list({ module })
      .then((page) => {
        if (!cancelled) {
          setState({ data: page.items, loading: false, error: null });
        }
      })
      .catch((error: Error) => {
        if (!cancelled) setState({ data: null, loading: false, error });
      });

    return () => {
      cancelled = true;
    };
  }, [module]);

  useEffect(load, [load]);

  return { ...state, reload: load };
}

export function useLatestReleases(limit = 5) {
  const [state, setState] = useState<AsyncState<PublicReleaseNote[]>>(
    initialState,
  );

  useEffect(() => {
    let cancelled = false;

    releaseNotesApi
      .latest(limit)
      .then((items) => {
        if (!cancelled) setState({ data: items, loading: false, error: null });
      })
      .catch((error: Error) => {
        if (!cancelled) setState({ data: null, loading: false, error });
      });

    return () => {
      cancelled = true;
    };
  }, [limit]);

  return state;
}

/**
 * Drives the post-release modal.
 *
 * The server decides what is unseen and returns the acknowledgement value, so
 * this hook holds no "have I shown it" state of its own beyond the current
 * render. Dismissing posts the marker and clears the list, and because the
 * marker lives server-side the modal does not come back on another device.
 *
 * A failure is swallowed on purpose. This runs on every entry into the app, and
 * a release-note outage must not put an error in front of someone trying to use
 * Extraction.
 */
export function useUnseenReleases(enabled = true) {
  const [items, setItems] = useState<PublicReleaseNote[]>([]);
  const [latestReleasedAt, setLatestReleasedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    releaseNotesApi
      .unseen()
      .then((response) => {
        if (cancelled) return;
        setItems(response.items);
        setLatestReleasedAt(response.latest_released_at);
      })
      .catch((error: unknown) => {
        if (error instanceof ReleaseNotesApiError && error.status === 401) {
          return;
        }
        // Intentionally quiet: see the docstring.
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const dismiss = useCallback(() => {
    const upTo = latestReleasedAt;
    setItems([]);
    if (!upTo) return;
    // Fire and forget. If the POST fails the modal simply reappears next load,
    // which is a better failure than blocking the dismiss on a network round trip.
    void releaseNotesApi.markSeen(upTo).catch(() => {});
  }, [latestReleasedAt]);

  return { items, latestReleasedAt, dismiss };
}
